import { ExpressionBuilder } from "@rocicorp/zero";
import { z } from "zod/v4";
import { Schema } from "../../zero/schema";
import { temporalPlainDateSchema } from "./zod";

export namespace TemporalHelpers {
  export type DateFormat = "monthDay" | "monthDayYear" | "longMonthDayYear" | "longWeekdayMonthDay";
  const millisecondsPerDay = 24 * 60 * 60 * 1000;

  export function plainDate(value: Temporal.PlainDate): Temporal.PlainDate;
  export function plainDate(value: string): Temporal.PlainDate;
  export function plainDate(epochMs: number): Temporal.PlainDate;
  export function plainDate(value: Temporal.PlainDate | string | number): Temporal.PlainDate {
    if (value instanceof Temporal.PlainDate) {
      return value;
    }
    if (typeof value === "number") {
      return Temporal.Instant.fromEpochMilliseconds(value).toZonedDateTimeISO("UTC").toPlainDate();
    }
    return Temporal.PlainDate.from(value);
  }

  export function plainTime(value: string): Temporal.PlainTime;
  export function plainTime(milliseconds: number): Temporal.PlainTime;
  export function plainTime(value: string | number): Temporal.PlainTime {
    if (typeof value === "number") {
      if (!Number.isInteger(value) || value < 0 || value >= millisecondsPerDay) {
        throw new RangeError("Time value must be an integer number of milliseconds since midnight");
      }

      const hour = Math.floor(value / (60 * 60 * 1000));
      const minute = Math.floor((value % (60 * 60 * 1000)) / (60 * 1000));
      const second = Math.floor((value % (60 * 1000)) / 1000);
      const millisecond = value % 1000;

      return Temporal.PlainTime.from({ hour, minute, second, millisecond });
    }

    return Temporal.PlainTime.from(value);
  }

  export function sqlTime(value: Temporal.PlainTime): number {
    return value.since(Temporal.PlainTime.from("00:00")).total("millisecond");
  }

  export function formatDate(
    value: Temporal.PlainDate | string | number,
    format: DateFormat = "monthDay",
  ): string {
    const date =
      value instanceof Temporal.PlainDate
        ? value
        : typeof value === "number"
          ? plainDate(value)
          : plainDate(value);

    switch (format) {
      case "monthDay":
        return date.toLocaleString("en-US", { month: "short", day: "numeric" });
      case "monthDayYear":
        return date.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric" });
      case "longMonthDayYear":
        return date.toLocaleString("en-US", { month: "long", day: "numeric", year: "numeric" });
      case "longWeekdayMonthDay":
        return date.toLocaleString("en-US", { weekday: "long", month: "long", day: "numeric" });
    }
  }

  export function formatTime(value: Temporal.PlainTime | string): string {
    const time = value instanceof Temporal.PlainTime ? value : plainTime(value);
    return time.toLocaleString(undefined, { hour: "numeric", minute: "2-digit" });
  }

  export function zonedDateTime(args: {
    date: Temporal.PlainDate | string;
    timeZone: string;
    time: Temporal.PlainTime | string;
  }): Temporal.ZonedDateTime;
  export function zonedDateTime(args: {
    epochMs: number;
    timeZone: string;
  }): Temporal.ZonedDateTime;
  export function zonedDateTime(
    args:
      | { date: Temporal.PlainDate | string; timeZone: string; time: Temporal.PlainTime | string }
      | { epochMs: number; timeZone: string },
  ): Temporal.ZonedDateTime {
    if ("epochMs" in args) {
      return Temporal.Instant.fromEpochMilliseconds(args.epochMs).toZonedDateTimeISO(args.timeZone);
    }
    return Temporal.PlainDate.from(args.date).toZonedDateTime({
      timeZone: args.timeZone,
      plainTime: Temporal.PlainTime.from(args.time),
    });
  }
}

export namespace DateRange {
  type DateRangeFormat = "monthDay" | "monthDayYear" | "longMonthDayYear";

  export const closedSchema = z
    .object({
      dateStart: temporalPlainDateSchema,
      dateEnd: temporalPlainDateSchema,
    })
    .refine(({ dateStart, dateEnd }) => Temporal.PlainDate.compare(dateStart, dateEnd) <= 0, {
      message: "End date must be same as or after start date",
      path: ["dateEnd"],
    });
  export type Closed = z.infer<typeof closedSchema>;

  export const openEndSchema = z
    .object({
      dateStart: temporalPlainDateSchema,
      dateEnd: temporalPlainDateSchema.nullable(),
    })
    .refine(
      ({ dateStart, dateEnd }) => {
        if (dateEnd === null) {
          return true;
        }
        return Temporal.PlainDate.compare(dateStart, dateEnd) <= 0;
      },
      {
        message: "End date must be same as or after start date",
        path: ["dateEnd"],
      },
    );
  export type OpenEnd = z.infer<typeof openEndSchema>;

  export const openStartSchema = z
    .object({
      dateStart: temporalPlainDateSchema.nullable(),
      dateEnd: temporalPlainDateSchema,
    })
    .refine(
      ({ dateStart, dateEnd }) => {
        if (dateStart === null) {
          return true;
        }
        return Temporal.PlainDate.compare(dateStart, dateEnd) <= 0;
      },
      {
        message: "End date must be same as or after start date",
        path: ["dateEnd"],
      },
    );
  export type OpenStart = z.infer<typeof openStartSchema>;

  export const openSchema = z
    .object({
      dateStart: temporalPlainDateSchema.nullable(),
      dateEnd: temporalPlainDateSchema.nullable(),
    })
    .refine(
      ({ dateStart, dateEnd }) => {
        if (dateStart === null || dateEnd === null) {
          return true;
        }
        return Temporal.PlainDate.compare(dateStart, dateEnd) <= 0;
      },
      {
        message: "End date must be same as or after start date",
        path: ["dateEnd"],
      },
    );
  export type Open = z.infer<typeof openSchema>;

  export function getSummary(range: Pick<DateRange.Closed, "dateStart" | "dateEnd">) {
    const dateStart = TemporalHelpers.plainDate(range.dateStart);
    const dateEnd = TemporalHelpers.plainDate(range.dateEnd);

    return {
      dateStart,
      dateEnd,
      durationDays: dateStart.until(dateEnd).days + 1,
    };
  }

  export function format(
    range: Pick<DateRange.Closed, "dateStart" | "dateEnd">,
    options: {
      dateFormat?: DateRangeFormat;
      separator?: string;
    } = {},
  ) {
    const { dateStart, dateEnd } = getSummary(range);

    return [
      TemporalHelpers.formatDate(dateStart, options.dateFormat ?? "monthDay"),
      TemporalHelpers.formatDate(dateEnd, options.dateFormat ?? "monthDay"),
    ].join(options.separator ?? " — ");
  }

  export function formatDuration(range: Pick<DateRange.Closed, "dateStart" | "dateEnd">) {
    const { durationDays } = getSummary(range);
    return `${durationDays} day${durationDays === 1 ? "" : "s"}`;
  }

  export function overlaps(
    left: Pick<DateRange.OpenEnd, "dateStart" | "dateEnd">,
    right: Pick<DateRange.OpenEnd, "dateStart" | "dateEnd">,
  ): boolean {
    const leftEnd = left.dateEnd ?? right.dateStart;
    const rightEnd = right.dateEnd ?? left.dateStart;

    return (
      Temporal.PlainDate.compare(left.dateStart, rightEnd) <= 0 &&
      Temporal.PlainDate.compare(right.dateStart, leftEnd) <= 0
    );
  }

  // TODO: Any way to make ExpressionBuilder be based on the required column names + types, instead of listing all relevant tables here?
  export const overlapsWhere = (
    eb: ExpressionBuilder<"mealPlan" | "nutritionProgram", Schema>,
    { dateStart, dateEnd }: DateRange.Closed,
  ) => {
    const startDate = TemporalHelpers.plainDate(dateStart);
    const endDate = TemporalHelpers.plainDate(dateEnd);
    return eb.or(
      eb.and(
        eb.cmp("dateStart", "<=", startDate),
        eb.or(eb.cmp("dateEnd", "IS", null), eb.cmp("dateEnd", ">=", startDate)),
      ),
      eb.and(
        eb.cmp("dateStart", "<=", endDate),
        eb.or(eb.cmp("dateEnd", "IS", null), eb.cmp("dateEnd", ">=", endDate)),
      ),
      eb.and(eb.cmp("dateStart", ">=", startDate), eb.cmp("dateEnd", "<=", endDate)),
    );
  };
}
